import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { apiJson } from "../../lib/api";
import { CodexTerminalFrame } from "../ui/CodexTerminalFrame";
import { useAuth } from "../../context/AuthContext";

type RuntimeState = "observing" | "acting" | "blocked" | "complete";

interface DelegationLane {
  lane: string;
  worker_kind: string;
  capabilities: string[];
  authority: string;
  contract: string;
}

interface RuntimeCharter {
  law: string;
  authority: string[];
  human_contract: string[];
  exceptions: string[];
  observability: string[];
  delegation: DelegationLane[];
}

interface RuntimeHeartbeat {
  interval_secs: number;
  last_tick_at?: string | null;
  tick_count: number;
}

interface RuntimeInfo {
  runtime_id: string;
  state: RuntimeState;
  scope: string;
  last_event?: string | null;
  updated_at: string;
  started_at: string;
  charter: RuntimeCharter;
  heartbeat: RuntimeHeartbeat;
}

interface StatusPayload {
  running: boolean;
  runtime: RuntimeInfo | null;
  summary: {
    pending: number;
    in_progress: number;
    done: number;
    blocked: number;
    total: number;
  };
  current_task: { id: string; prompt: string } | null;
  next_task: { id: string; prompt: string } | null;
}

interface ArbiterTask {
  source: string;
  runtime_id: string | null;
  runtime_kind: string;
  id: string;
  title: string;
  status: string;
  lane?: string | null;
  authority?: string | null;
  requested_by?: string | null;
  updated_at: string;
}

interface TaskPayload {
  ok: boolean;
  tasks: ArbiterTask[];
  count: number;
}

interface RequestResponse {
  ok: boolean;
  accepted: boolean;
  error?: string;
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ArbiterWorkbench() {
  const { user, authRequired } = useAuth();
  const [arbiter, setArbiter] = useState<StatusPayload | null>(null);
  const [autopilot, setAutopilot] = useState<StatusPayload | null>(null);
  const [tasks, setTasks] = useState<ArbiterTask[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [draft, setDraft] = useState("note: answer the next human or agent question from the arbiter lane");
  const [lane, setLane] = useState("governance");
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [arbiterData, autopilotData, taskData] = await Promise.all([
      apiJson<StatusPayload>("/api/arbiter/status"),
      apiJson<StatusPayload>("/api/autopilot/status"),
      apiJson<TaskPayload>("/api/arbiter/tasks"),
    ]);
    setArbiter(arbiterData);
    setAutopilot(autopilotData);
    setTasks(taskData.tasks ?? []);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    const interval = window.setInterval(() => {
      load().catch(() => {});
    }, 5000);
    return () => window.clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!selectedTaskId && tasks.length > 0) {
      setSelectedTaskId(tasks[0].id);
    }
  }, [selectedTaskId, tasks]);

  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) ?? null,
    [selectedTaskId, tasks]
  );

  const submit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitMessage(null);
    try {
      const result = await apiJson<RequestResponse>("/api/arbiter/request", {
        method: "POST",
        body: JSON.stringify({
          prompt: draft,
          lane,
          authority: "admin-arbiter",
        }),
      });
      if (!result.ok || !result.accepted) {
        throw new Error(result.error || "Arbiter did not accept the request.");
      }
      setSubmitMessage("Arbiter accepted the request.");
      await load();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit arbiter request.");
    } finally {
      setSubmitting(false);
    }
  }, [draft, lane, load]);

  if (authRequired && !user?.admin) {
    return (
      <div style={styles.root}>
        <div style={styles.denied}>Arbiter is admin-only.</div>
      </div>
    );
  }

  const runtime = arbiter?.runtime ?? null;
  const lanes = runtime?.charter.delegation ?? [];

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>Mission Control / Arbiter</div>
          <h1 style={styles.title}>The standing brain of OpenTool.</h1>
          <p style={styles.subtitle}>
            The arbiter observes the system, answers questions, routes work by lane, and stays live
            on a timed heartbeat. It is admin-only because it carries the broadest authority.
          </p>
        </div>
        <div style={styles.badges}>
          <Badge label="Arbiter" value={runtime?.state ?? "offline"} />
          <Badge
            label="Heartbeat"
            value={
              runtime
                ? `${runtime.heartbeat.interval_secs}s · ${runtime.heartbeat.tick_count} ticks`
                : "offline"
            }
          />
        </div>
      </div>

      <div style={styles.grid}>
        <section style={styles.panel}>
          <div style={styles.panelLabel}>Standing Runtime</div>
          {runtime ? (
            <CodexTerminalFrame title="arbiter" prompt={`runtime ${runtime.runtime_id}`}>
              <div style={styles.rows}>
                <Row label="Law" value={runtime.charter.law} />
                <Row label="Scope" value={runtime.scope} />
                <Row label="Last tick" value={fmtDate(runtime.heartbeat.last_tick_at)} />
                <Row label="Last event" value={runtime.last_event ?? "—"} />
                <Row
                  label="Current"
                  value={arbiter?.current_task?.prompt ?? "observing / waiting for intake"}
                />
                <Row
                  label="Next"
                  value={arbiter?.next_task?.prompt ?? "—"}
                />
              </div>
            </CodexTerminalFrame>
          ) : (
            <div style={styles.empty}>Arbiter runtime not started yet.</div>
          )}
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Admin Intake</div>
          <label style={styles.field}>
            <span>Lane</span>
            <input value={lane} onChange={(e) => setLane(e.target.value)} style={styles.input} />
          </label>
          <label style={styles.field}>
            <span>Request</span>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={styles.textarea}
            />
          </label>
          <button onClick={submit} disabled={submitting} style={styles.button}>
            {submitting ? "Submitting…" : "Send To Arbiter"}
          </button>
          {submitMessage ? <div style={styles.success}>{submitMessage}</div> : null}
          {submitError ? <div style={styles.error}>{submitError}</div> : null}
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Delegation Lanes</div>
          <div style={styles.laneList}>
            {lanes.map((entry) => (
              <div key={entry.lane} style={styles.laneCard}>
                <div style={styles.laneTitle}>
                  <strong>{entry.lane}</strong>
                  <span>{entry.worker_kind}</span>
                </div>
                <div style={styles.laneBody}>{entry.contract}</div>
                <div style={styles.laneMeta}>{entry.authority}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>System Task Index</div>
          <div style={styles.taskLayout}>
            <div style={styles.taskList}>
              {tasks.map((task) => (
                <button
                  key={`${task.source}-${task.id}`}
                  style={{
                    ...styles.taskButton,
                    ...(selectedTaskId === task.id ? styles.taskButtonActive : null),
                  }}
                  onClick={() => setSelectedTaskId(task.id)}
                >
                  <div style={styles.taskTop}>
                    <span>{task.source}</span>
                    <span>{task.status}</span>
                  </div>
                  <strong>{task.title}</strong>
                  <div style={styles.taskMeta}>{fmtDate(task.updated_at)}</div>
                </button>
              ))}
            </div>
            <div style={styles.taskDetail}>
              {selectedTask ? (
                <>
                  <h3 style={styles.detailTitle}>{selectedTask.title}</h3>
                  <Detail label="Source" value={selectedTask.source} />
                  <Detail label="Runtime" value={selectedTask.runtime_kind} />
                  <Detail label="Lane" value={selectedTask.lane || "—"} />
                  <Detail label="Authority" value={selectedTask.authority || "—"} />
                  <Detail label="Requested by" value={selectedTask.requested_by || "—"} />
                  <Detail label="Updated" value={fmtDate(selectedTask.updated_at)} />
                </>
              ) : (
                <div style={styles.empty}>Select a task to inspect it.</div>
              )}
            </div>
          </div>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelLabel}>Relationship</div>
          <div style={styles.rows}>
            <Row label="OpenTool" value="product and control surface" />
            <Row label="tool_os" value="workspace experience and desktop shell" />
            <Row label="Autopilot" value={autopilot?.runtime?.state ?? "offline"} />
            <Row label="Arbiter" value={runtime?.state ?? "offline"} />
          </div>
        </section>
      </div>
    </div>
  );
}

function Badge({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.badge}>
      <span style={styles.badgeLabel}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{value}</span>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>{value}</div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  root: { padding: 28, color: "#e5edf9" },
  header: { display: "flex", justifyContent: "space-between", gap: 18, marginBottom: 22 },
  kicker: {
    textTransform: "uppercase",
    letterSpacing: "0.22em",
    fontSize: 11,
    color: "#7dd3fc",
    marginBottom: 8,
  },
  title: { margin: 0, fontSize: 34, lineHeight: 1.1 },
  subtitle: { marginTop: 12, maxWidth: 760, color: "#94a3b8", lineHeight: 1.7 },
  badges: { display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" },
  badge: {
    border: "1px solid rgba(125,211,252,0.18)",
    background: "rgba(15,23,42,0.72)",
    borderRadius: 999,
    padding: "10px 14px",
    display: "flex",
    gap: 8,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  },
  badgeLabel: { color: "#7dd3fc" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 18 },
  panel: {
    border: "1px solid rgba(148,163,184,0.16)",
    background: "rgba(15,23,42,0.64)",
    borderRadius: 20,
    padding: 18,
  },
  panelLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.18em",
    color: "#fca5a5",
    marginBottom: 14,
  },
  rows: { display: "grid", gap: 10 },
  row: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    gap: 10,
    alignItems: "start",
  },
  rowLabel: { color: "#7dd3fc", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em" },
  rowValue: { color: "#dbeafe", lineHeight: 1.6 },
  field: { display: "grid", gap: 8, marginBottom: 12, color: "#93c5fd" },
  input: {
    background: "#020617",
    color: "#e2e8f0",
    border: "1px solid rgba(148,163,184,0.2)",
    borderRadius: 10,
    padding: "10px 12px",
  },
  textarea: {
    background: "#020617",
    color: "#e2e8f0",
    border: "1px solid rgba(148,163,184,0.2)",
    borderRadius: 10,
    padding: "12px",
    minHeight: 120,
    resize: "vertical",
  },
  button: {
    background: "#38bdf8",
    color: "#082f49",
    border: "none",
    borderRadius: 10,
    padding: "12px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  success: { marginTop: 10, color: "#4ade80" },
  error: { marginTop: 10, color: "#fca5a5" },
  laneList: { display: "grid", gap: 10 },
  laneCard: {
    border: "1px solid rgba(148,163,184,0.14)",
    borderRadius: 14,
    padding: 14,
    background: "rgba(2,6,23,0.5)",
  },
  laneTitle: { display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 8 },
  laneBody: { color: "#cbd5e1", marginBottom: 6 },
  laneMeta: { color: "#7dd3fc", fontSize: 13 },
  taskLayout: { display: "grid", gridTemplateColumns: "280px 1fr", gap: 16 },
  taskList: { display: "grid", gap: 8, maxHeight: 420, overflow: "auto" },
  taskButton: {
    textAlign: "left",
    border: "1px solid rgba(148,163,184,0.12)",
    background: "rgba(2,6,23,0.45)",
    color: "#e2e8f0",
    borderRadius: 12,
    padding: 12,
    cursor: "pointer",
  },
  taskButtonActive: {
    borderColor: "rgba(56,189,248,0.55)",
    boxShadow: "0 0 0 1px rgba(56,189,248,0.25) inset",
  },
  taskTop: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 8,
    color: "#7dd3fc",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  taskMeta: { color: "#94a3b8", fontSize: 12, marginTop: 6 },
  taskDetail: {
    border: "1px solid rgba(148,163,184,0.14)",
    borderRadius: 14,
    padding: 16,
    background: "rgba(2,6,23,0.35)",
    minHeight: 240,
  },
  detailTitle: { marginTop: 0, marginBottom: 16 },
  empty: { color: "#94a3b8" },
  denied: {
    padding: 24,
    borderRadius: 18,
    background: "rgba(127,29,29,0.25)",
    border: "1px solid rgba(248,113,113,0.2)",
  },
};
